import { useAuthStore } from '@/store/useAuthStore'

export const AUTH_LOGOUT_STORAGE_KEY = 'auth-logout'

const SESSION_UPDATE_GRACE_MS = 5_000

let redirecting = false
let sessionUpdate: Promise<unknown> | null = null

export function updateSession<T>(update: () => Promise<T>): Promise<T> {
  const pending = Promise.resolve().then(update)
  let timeout: ReturnType<typeof setTimeout>
  const gracePeriod = new Promise<void>((resolve) => {
    timeout = setTimeout(resolve, SESSION_UPDATE_GRACE_MS)
  })
  const waiting = Promise.race([pending, gracePeriod])
    .then(
      () => undefined,
      () => undefined,
    )
    .finally(() => {
      clearTimeout(timeout)
      if (sessionUpdate === waiting) sessionUpdate = null
    })
  sessionUpdate = waiting
  return pending
}

export function isRevokedSession(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false
  const data = value as { code?: unknown; detail?: unknown; extra?: { code?: unknown } }
  return [data.code, data.detail, data.extra?.code].some(
    (code) => code === 'account_inactive' || code === 'session_revoked' || code === 'password_expired',
  )
}

export function endSession(broadcast = true, expectedToken?: string | null): void {
  if (expectedToken !== undefined) {
    if (sessionUpdate) {
      // Avoid stale revocation racing with password-change token rotation by deferring
      // logout until the update settles or the 5-second grace period expires.
      void sessionUpdate.then(
        () => endSession(broadcast, expectedToken),
        () => endSession(broadcast, expectedToken),
      )
      return
    }
    if (useAuthStore.getState().authToken !== expectedToken) return
    try {
      const storageName = useAuthStore.persist.getOptions().name
      const persisted = storageName ? JSON.parse(localStorage.getItem(storageName) ?? 'null') : null
      if (persisted?.state?.authToken && persisted.state.authToken !== expectedToken) {
        void useAuthStore.persist.rehydrate()
        return
      }
    } catch {
      // The storage check prevents stale revocation from logging out tokens renewed
      // in another tab. If storage is unavailable, enforce revocation of the matching in-memory token.
    }
  }
  if (redirecting) return
  redirecting = true
  useAuthStore.getState().logout()
  if (broadcast) {
    try {
      localStorage.setItem(AUTH_LOGOUT_STORAGE_KEY, JSON.stringify({ id: crypto.randomUUID(), token: expectedToken }))
    } catch {
      // Logout still works when browser storage is unavailable.
    }
  }
  window.location.replace('/login')
}
